import { withSentry } from "@sentry/remix";
import { LinksFunction, MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from "@remix-run/react";
import { GeneralErrorBoundary } from "./components/GeneralErrorBoundary";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useEffect, useState } from "react";
import styles from "./tailwind.css?url";
import { honeypot } from "~/honeypot.server";
import { HoneypotProvider } from "remix-utils/honeypot/react";
import { ExternalScripts } from "remix-utils/external-scripts";
import { setupStagewise } from "./stagewiseConfig";
import { useAppVersion } from "~/hooks/useAppVersion";
import { FetchError } from "ofetch";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import sonnerStyles from "sonner/dist/styles.css?url";

export const links: LinksFunction = () => [
  {
    rel: "preconnect",
    href: "https://fonts.googleapis.com",
  },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
  { rel: "stylesheet", href: styles },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap",
  },
  {
    rel: "stylesheet",
    href: sonnerStyles,
  },
];

export const meta: MetaFunction = (_data) => {
  return [
    { title: "PostCo: Returns Center" },
    { name: "viewport", content: "width=device-width, initial-scale=1" },
    { charSet: "UTF-8" },
    { httpEquiv: "X-UA-Compatible", content: "ie=edge" },
  ];
};

// this makes window.ENV type safe
declare global {
  interface Window {
    ENV: {
      POSTCO_APP_SERVER_URL: string;
      RAILS_WEBSOCKET_URL: string;
      R2_CUSTOM_DOMAIN: string;
      OLD_CUSTOMER_APP_URL: string;
      OLD_EMBED_CUSTOMER_APP_URL: string;
      SENTRY_DSN: string;
      REMIX_APP_ENVIRONMENT: string;
      STRIPE_MY_PUBLISHABLE_KEY: string;
      STRIPE_SG_PUBLISHABLE_KEY: string;
      NEW_CUSTOMER_APP_URL: string;
      PUBLIC_ASSETS_URL: string;
    };
    APP_VERSION: string;
  }
}

// setting browser env based on this https://remix.run/docs/en/main/guides/envvars#browser-environment-variables
export async function loader() {
  return {
    honeypotInputProps: honeypot.getInputProps(),
    ENV: {
      POSTCO_APP_SERVER_URL: process.env.POSTCO_APP_SERVER_URL,
      RAILS_WEBSOCKET_URL: process.env.RAILS_WEBSOCKET_URL,
      R2_CUSTOM_DOMAIN: process.env.R2_CUSTOM_DOMAIN,
      OLD_CUSTOMER_APP_URL: process.env.OLD_CUSTOMER_APP_URL,
      OLD_EMBED_CUSTOMER_APP_URL: process.env.OLD_EMBED_CUSTOMER_APP_URL,
      SENTRY_DSN: process.env.SENTRY_DSN,
      REMIX_APP_ENVIRONMENT: process.env.REMIX_APP_ENVIRONMENT,
      STRIPE_MY_PUBLISHABLE_KEY: process.env.STRIPE_MY_PUBLISHABLE_KEY,
      STRIPE_SG_PUBLISHABLE_KEY: process.env.STRIPE_SG_PUBLISHABLE_KEY,
      NEW_CUSTOMER_APP_URL: process.env.NEW_CUSTOMER_APP_URL,
      PUBLIC_ASSETS_URL: process.env.PUBLIC_ASSETS_URL,
    },
    version: process.env.CUSTOMER_APPLICATION_VERSION as string,
  };
}

function Document({
  children,
  env = {},
  version,
}: {
  children: React.ReactNode;
  env?: Record<string, string>;
  version: string;
}) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <Meta />
        <Links />
        <ExternalScripts />
      </head>
      <body>
        {/* The div below is to make sure the Popover from HeadlessUI close if click outside Popover */}
        <div>
          {children}
          <script
            dangerouslySetInnerHTML={{
              __html: `window.ENV = ${JSON.stringify(env)}`,
            }}
          />
          <script
            dangerouslySetInnerHTML={{
              __html: `window.APP_VERSION = "${version}"`,
            }}
          />
          <Toaster richColors theme="light" visibleToasts={5} />
          <ScrollRestoration />
          <Scripts />
        </div>
      </body>
    </html>
  );
}

function App() {
  // setupStagewise();

  const { ENV, honeypotInputProps, version } = useLoaderData<typeof loader>();
  const { newVersionAvailableFromRemixBackend } = useAppVersion();

  function triggerNewVersionToast() {
    toast.warning("A fresh update is ready for you!", {
      description:
        "We've just released some improvements. This page will automatically refresh in 10 seconds to load the new version.",
      action: {
        label: "Refresh Now",
        onClick: () => window.location.reload(),
      },
      duration: Infinity,
    });
  }

  function triggerUpdateOngoingToast() {
    toast.warning("We're making some improvements!", {
      description:
        "We're rolling out a quick update to enhance your experience. To ensure a smooth transition, all actions are temporarily disabled. The page will automatically refresh in about a minute or two. Thanks for your patience!",
      duration: Infinity,
    });
  }

  useEffect(() => {
    if (newVersionAvailableFromRemixBackend) {
      triggerNewVersionToast();
    }
  }, [newVersionAvailableFromRemixBackend]);

  function handleVersionError(error: unknown) {
    if (error instanceof FetchError && (error.status === 427 || error.response?.status === 427)) {
      triggerUpdateOngoingToast();
    }
  }

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: handleVersionError,
        }),
        mutationCache: new MutationCache({
          onError: handleVersionError,
        }),
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 60 * 1000,
          },
        },
      }),
  );

  return (
    <Document env={ENV} version={version}>
      <QueryClientProvider client={queryClient}>
        <HoneypotProvider {...honeypotInputProps}>
          <Outlet />
        </HoneypotProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Document>
  );
}

export default withSentry(App);

export function ErrorBoundary() {
  return (
    <Document version="unknown">
      <GeneralErrorBoundary />
    </Document>
  );
}
